import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DEFAULT_SETTINGS, THEME_PRESETS, FONT_OPTIONS,
  FONT_SIZE_OPTIONS, DENSITY_OPTIONS, isLightBackground,
} from "@/lib/themes";

const ThemeContext = createContext(null);

const STORAGE_KEY = "nova-settings-v1";

function loadFonts(id) {
  const opt = FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];
  const familyParam = (name) => name.replace(/\s+/g, "+");
  const hrefId = "nova-font-link";
  const href = `https://fonts.googleapis.com/css2?family=${familyParam(opt.heading)}:wght@300;400;500;600;700&family=${familyParam(opt.body)}:wght@300;400;500;600;700&display=swap`;
  let link = document.getElementById(hrefId);
  if (!link) {
    link = document.createElement("link");
    link.id = hrefId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
  return opt;
}

function applyThemeToRoot(settings) {
  const root = document.documentElement;
  const c = settings.colors;
  const set = (k, v) => root.style.setProperty(k, v);

  set("--nova-bg", c.bg);
  set("--nova-surface", c.surface);
  set("--nova-surface-alt", c.surfaceAlt);
  set("--nova-border", c.border);
  set("--nova-border-strong", c.borderStrong);
  set("--nova-text", c.text);
  set("--nova-text-muted", c.textMuted);
  set("--nova-text-faint", c.textFaint);
  set("--nova-primary", c.primary);
  set("--nova-primary-hover", c.primaryHover);
  set("--nova-primary-text", c.primaryText);
  set("--nova-accent", c.accent);
  set("--nova-danger", c.danger);

  // Fonts
  const fonts = loadFonts(settings.fonts?.id || DEFAULT_SETTINGS.fonts.id);
  set("--nova-font-heading", `"${fonts.heading}", sans-serif`);
  set("--nova-font-body", `"${fonts.body}", sans-serif`);

  // Font size
  const size = FONT_SIZE_OPTIONS.find((f) => f.id === (settings.font_size || "normal")) || FONT_SIZE_OPTIONS[1];
  set("--nova-font-scale", String(size.scale));
  root.style.fontSize = `${size.scale * 16}px`;

  // Density
  const density = DENSITY_OPTIONS.find((d) => d.id === (settings.density || "comfortable")) || DENSITY_OPTIONS[1];
  set("--nova-density", String(density.pad));

  // Light-mode flag class
  if (isLightBackground(c.bg)) {
    root.classList.add("nova-light");
  } else {
    root.classList.remove("nova-light");
  }
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    } catch (err) {
      console.warn("Kon opgeslagen thema niet laden:", err);
    }
    return DEFAULT_SETTINGS;
  });

  // Apply immediately on mount and whenever settings change
  useEffect(() => {
    applyThemeToRoot(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (err) {
      console.warn("Kon thema-instellingen niet opslaan:", err);
    }
  }, [settings]);

  // Load user settings from backend after login
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled && data && data.preset) {
          setSettings({ ...DEFAULT_SETTINGS, ...data });
        }
      } catch (err) {
        console.warn("Kon thema-instellingen van server niet ophalen:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const persist = useCallback(async (next) => {
    setSettings(next);
    try {
      await api.put("/settings", next);
    } catch (err) {
      console.warn("Kon thema-instellingen niet synchroniseren:", err);
    }
  }, []);

  const applyPreset = useCallback((presetId) => {
    const preset = THEME_PRESETS[presetId];
    if (!preset) return;
    persist({ ...settings, preset: presetId, colors: preset.colors });
  }, [settings, persist]);

  const setColor = useCallback((key, value) => {
    persist({ ...settings, preset: "custom", colors: { ...settings.colors, [key]: value } });
  }, [settings, persist]);

  const setFontFamily = useCallback((id) => {
    const opt = FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];
    persist({ ...settings, fonts: { id: opt.id, heading: opt.heading, body: opt.body } });
  }, [settings, persist]);

  const setFontSize = useCallback((id) => persist({ ...settings, font_size: id }), [settings, persist]);
  const setDensity = useCallback((id) => persist({ ...settings, density: id }), [settings, persist]);

  const resetDefaults = useCallback(() => persist(DEFAULT_SETTINGS), [persist]);

  const value = useMemo(
    () => ({ settings, applyPreset, setColor, setFontFamily, setFontSize, setDensity, resetDefaults }),
    [settings, applyPreset, setColor, setFontFamily, setFontSize, setDensity, resetDefaults]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
